<template>
  <template v-if="cinemasAreOffline">
    <div class="container">
      <div class="b-cinema-offline">
        <div class="b-cinema-offline__body">
          <div class="b-cinema-offline__main">
            <i class="icon-cinema-offline"></i>
            <div>
              <h4 class="mb-24">{{ dictionary.cinemaOfflineTitle }}</h4>
              <h5>{{ dictionary.cinemaOfflineSubTitle }}</h5>
            </div>
          </div>
        </div>
      </div>
    </div>
  </template>
  <template v-else>
    <div class="container">
      <div class="s-playing-now__options">
        <CinemaFilters
          v-model="selectedFilters"
          :placeholder-cinemas="placeholderCinemas"
          :placeholder-genres="placeholderGenres"
          :placeholder-room-type="placeholderRoomType"
          :filters-data="filtersData"
          :show-filters="showFilters"
          @filter-changed="filterChanged" />

        <LayoutViewSwitch v-if="desktop" @change-view="changeView" />
        <button
          v-if="!desktop"
          type="button"
          class="s-playing-now__filters-btn"
          @click="() => (showMobileFiltersModal = true)"></button>
      </div>
      <keep-alive>
        <Swiper
          v-if="filteredRecords.length && horizontalCurrentView"
          ref="swiper"
          :navigation="{
            nextEl: '.s-playing-now__next',
            prevEl: '.s-playing-now__prev',
          }"
          :pagination="{
            el: '.s-playing-now__pagination',
            type: 'bullets',
            clickable: true,
          }"
          :breakpoints="{
            320: {
              slidesPerView: 1.05,
              spaceBetween: 8,
            },
            576: {
              slidesPerView: 1.5,
              spaceBetween: 16,
            },
            768: {
              slidesPerView: 2.25,
              spaceBetween: 16,
            },
            992: {
              slidesPerView: 2.75,
              spaceBetween: 32,
            },
            1279: {
              slidesPerView: 'auto',
              spaceBetween: 32,
            },
          }">
          <SwiperSlide
            v-for="(record, index) in filteredRecords"
            :id="record.id"
            :key="record.id"
            :style="removeTrailerWidth(index, record)"
            :class="movieCardVideoClass(index, record)">
            <MovieCard
              :ref="`movie-card-${record.id}`"
              :title="record.title"
              :subtitle="record.subTitle"
              :description="record.desc"
              :image="record.thumb"
              :pg="String(record.pg)"
              :duration="record.dur"
              :dolby="record.dolby"
              :three-dimensions="record.threeDimensions"
              :imax="record.imax"
              :imax3D="record.imax3D"
              :sphera="false"
              :genres="recordGenres(record)"
              :dictionary="dictionary"
              :due="record.due"
              :video-id="record.vid"
              :play="playFirsTrailer(index) && playVideoInCard"
              :movie-id="record.movieId"
              :transl="record.transl"
              :movie-url="record.url"
              :elements-show-options="{
                showPremier: false,
                showTrailerCta: true,
                showBookingCta: true,
                showDuration: true,
                showGenres: true,
                showDesc: false,
                showMoreCta: true,
                posterCardDimensions: {},
              }"
              :cinema-id="cinemaId"
              @play-trailer-in-full-mode="playTrailerInFullMode"></MovieCard>
          </SwiperSlide>

          <div class="swiper__nav">
            <div
              v-show="desktop"
              class="swiper-button-prev s-playing-now__prev"></div>
            <div class="swiper-pagination s-playing-now__pagination"></div>
            <div
              v-show="desktop"
              class="swiper-button-next s-playing-now__next"></div>
          </div>
        </Swiper>

        <div v-else-if="!horizontalCurrentView" class="s-playing-now__app">
          <div class="s-grid s-playing-now__grid">
            <div
              v-for="record in filteredRecords"
              :key="record.id"
              class="s-grid__col-3">
              <MovieCard
                :title="record.title"
                :subtitle="record.subTitle"
                :image="record.thumb"
                :pg="String(record.pg)"
                :duration="record.dur"
                :dolby="record.dolby"
                :imax="record.imax"
                :imax3D="record.imax3D"
                :movie-url="record.url"
                :genres="recordGenres(record)"
                :dictionary="dictionary"
                :video-id="record.vid"
                :play="false"
                :movie-id="record.movieId"></MovieCard>
            </div>
          </div>
        </div>
      </keep-alive>
      <div class="s-playing-now__btn-area clearfix">
        <a :href="configuration.stripBtnUrl" class="s-playing-now__cta">
          <span>{{ dictionary.stripBtnTitle }}</span>
        </a>
      </div>
      <ModalOverlay
        v-model="showTrailerModal"
        video-modal
        :swiper-data="[videoId]"
        background-class="b-pop-up--video b-pop-up__holder"
        header-background-class="b-pop-up__header">
        <template #header>
          <div class="b-pop-up__header__title">Trailer</div>
        </template>
      </ModalOverlay>
      <ModalOverlay v-model="showMobileFiltersModal" size="player">
        <template #header>
          <i class="icon-filters"></i>
          <div class="b-pop-up__header__title">{{ dictionary.filters }}</div>
        </template>
        <template #body>
          <div class="b-pop-up__content">
            <CinemaFilters
              v-model="selectedFilters"
              :placeholder-cinemas="placeholderCinemas"
              :placeholder-genres="placeholderGenres"
              :placeholder-room-type="placeholderRoomType"
              :filters-data="filtersData"
              :show-filters="visibleFilters"
              @filter-changed="filterChanged" />
            <div class="b-pop-up__btn-area">
              <button
                type="submit"
                class="b-pop-up__btn-mobile"
                @click="closeFilterModal">
                {{ dictionary.result }}
              </button>
              <button
                type="reset"
                class="b-pop-up__btn-reset"
                @click="resetMoviesFilters">
                {{ dictionary.clearFilter }}
              </button>
            </div>
          </div>
        </template>
      </ModalOverlay>
    </div>
  </template>
</template>

<script setup>
import { computed, nextTick, onBeforeMount, ref } from 'vue'
import { Swiper, SwiperSlide } from 'swiper/vue'
import MovieCard from '../components/MovieCard.vue'
import CinemaFilters from '../components/CinemaFilters.vue'
import LayoutViewSwitch from '../components/LayoutViewSwitch.vue'
import ModalOverlay from '../components/ModalOverlay.vue'
import { getData, getRecordGenresWithDislay } from '../../lib/lib'
import useViewport from '../../features/viewport.js'
const { desktop } = useViewport()

const {
  dictionary,
  records: movies,
  filters,
  configuration,
  screens: allScreens,
} = getData('nowPlayingData')

const placeholderCinemas = dictionary.placeholderCinemas
  ? dictionary.placeholderCinemas
  : 'All'
const placeholderGenres = dictionary.placeholderGenres
  ? dictionary.placeholderGenres
  : 'All'
const placeholderRoomType = dictionary.placeholderRoomType
  ? dictionary.placeholderRoomType
  : 'All'

const records = ref([])
const genres = ref([])
const roomTypes = ref([])
const currentView = ref('horizontal')
const filteredRecords = ref([])
const showTrailerModal = ref(false)
const videoId = ref('')
const showMobileFiltersModal = ref(false)

const hideFilters = ref(
  configuration.hideFilters ? configuration.hideFilters : []
)

const defaultFilters = ['cinemas', 'genres', 'roomTypes']
const visibleFilters = ref(
  defaultFilters.filter((item) => !hideFilters.value.includes(item))
)

const selectedFilters = ref({
  cinemas: 'All',
  genres: 'All',
  roomTypes: 'All',
})
const filtersData = ref({})

const cinemaId = ref(null)

const cinemasAreOffline = computed(() => {
  return filters.cinemas.every((i) => !i.isOnline)
})

const horizontalCurrentView = computed(() => {
  return currentView.value === 'horizontal'
})

const showFilters = computed(() => {
  const mobileFrontFilter = visibleFilters.value.includes('cinemas')
    ? ['cinemas']
    : [visibleFilters.value[0]]

  return desktop.value ? visibleFilters.value : mobileFrontFilter
})

onBeforeMount(() => {
  records.value = [...movies]
  genres.value = [...filters.genres]
  roomTypes.value = [...filters.roomTypes]
  filteredRecords.value = [...movies]
  filtersData.value = filters

  cinemaId.value = getCinemaId()

  // Ticket: 10135036. Remove selectedCinema from sessionStorage because of a conflict
  // between preSelectedCinema and selectedCinema in the PreBookingStepApp.
  if (sessionStorage.getItem('selectedCinema')) {
    sessionStorage.removeItem('selectedCinema')
    sessionStorage.removeItem('selectedDate')
  }
})

const getCinemaId = () => {
  const isCinemaPage = hideFilters.value.some((i) => i.includes('cinema'))
  const cinemaEntryIsUnique = filters.cinemas.length === 1

  return isCinemaPage && cinemaEntryIsUnique ? filters.cinemas[0]?.value : null
}

const recordGenres = (record) => {
  return getRecordGenresWithDislay(genres, record.genreId)
}

const playFirsTrailer = (index) => {
  return index === 0
}
const changeView = (viewType) => {
  currentView.value = viewType
}
const filterChanged = (filters) => {
  filteredRecords.value = []
  cinemaId.value = filters.cinemas

  setTimeout(() => {
    if (Object.values(filters).every((filter) => filter === 'All')) {
      filteredRecords.value = records.value
    } else {
      filteredRecords.value = filterMoviesData(
        JSON.parse(JSON.stringify(filters))
      )
    }
  }, 1)
}

// creates a map of movieId to genreId
// it's used to filter movies by genre in the filter component
// cause screens missing this info

const genresMap = computed(() => {
  return records.value.reduce((acc, cur) => {
    const found = acc.find((item) => item.movieId === cur.id)
    if (!found) {
      acc.push({ movieId: cur.id, genreId: cur.genreId })
    }
    return acc
  }, [])
})

const filterMoviesData = (filters) => {
  const filteredRecords = allScreens.filter((record) => {
    const cinemaMatch =
      filters.cinemas === 'All' || record.cinemaId === filters.cinemas

    const roomTypeMatch =
      filters.roomTypes === 'All' ||
      record.roomTypes.includes(filters.roomTypes)

    const genreMatch =
      filters.genres === 'All' ||
      genresMap.value.some(
        (genre) =>
          genre.movieId === record.scheduledFilmId &&
          filters.genres === genre.genreId
      )

    return cinemaMatch && roomTypeMatch && genreMatch
  })

  return records.value.filter((record) => {
    return filteredRecords.some((i) => i.scheduledFilmId === record.movieId)
  })
}

const removeTrailerWidth = (index, record) => {
  if (desktop.value) {
    if (index === 0 && !record.vid) {
      return 'max-width: 356px;'
    }
  }
  return ''
}
const movieCardVideoClass = (index, record) => {
  if (playFirsTrailer(index) && record.vid) {
    return 'swiper-slide--video'
  }
  return ''
}
const playTrailerInFullMode = async (id) => {
  videoId.value = id
  await nextTick()
  showTrailerModal.value = true
}

const closeFilterModal = () => {
  showMobileFiltersModal.value = false
}

const resetMoviesFilters = () => {
  selectedFilters.value = {
    cinemas: 'All',
    genres: 'All',
    roomTypes: 'All',
  }
  filterChanged(selectedFilters.value)
}

const playVideoInCard = computed(() => {
  return desktop.value && !showTrailerModal.value
})
</script>
